/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import {
  Button,
  Chip,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { DefaultSelectV2, DialogDefaultV2 } from '../../../components/v2';
import { fetchEquipamentosGrouped } from '../Equipamentos/fetchEquipamentosGrouped';
import { fetchEstoqueGrouped } from '../Estoque/fetchEstoqueGrouped';
import { isItemOutro } from './statusLabels';
import PedidoVinculadoForm, { emptyPedidoVinculado } from './PedidoVinculadoForm';
import EscolherPatrimoniosDialog from './EscolherPatrimoniosDialog';

const COPY = {
  APROVADO: {
    title: 'Confirmar aprovação',
    paragraphs: [
      'Este item será atendido com o estoque e fica Aprovado.',
      'Ele só sai daqui quando você criar uma entrega na aba Entregas — sozinho ou agrupado com outros aprovados.',
    ],
  },
  REJEITADO: {
    title: 'Confirmar rejeição',
    paragraphs: [
      'Este item não será atendido. O solicitante vê o motivo no app.',
      'Não gera entrega nem pedido de compra.',
      'Confirmar grava agora.',
    ],
  },
  PARA_PEDIDO: {
    title: 'Pedido vinculado',
    paragraphs: [
      'Este item não usa o estoque atual. Confirmar cria o pedido de almoxarifado já ligado a este item.',
      'Não há status de pedido. Depois de gravar, o item já pode entrar numa entrega.',
    ],
  },
};

const pedidoSchema = {
  produto: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  local: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  quantidade: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  valorUnitario: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  fornecedor: { presence: { allowEmpty: false, message: 'é obrigatório' } },
};

const GROUPED_PAGE_SIZE = 100;
const GROUPED_MAX_ROWS = 500;

export const itemNeedsPedidoVinculado = (item) =>
  !!item && item.tipoItem === 'PRODUTO' && !isItemOutro(item);

const resolveTipoEquipamento = (item, vinculo) => {
  if (vinculo && vinculo.tipoItem === 'EQUIPAMENTO') return vinculo.idItem;
  if (item && item.tipoItem === 'EQUIPAMENTO' && item.idItem) return item.idItem;
  return null;
};

const resolveNeedsEquipamento = (item, decisao, vinculo) => {
  if (decisao !== 'APROVADO') return false;
  if (vinculo && vinculo.tipoItem === 'EQUIPAMENTO') return true;
  return !!item && item.tipoItem === 'EQUIPAMENTO' && !isItemOutro(item);
};

const resolveNeedsLocalAvulso = (decisao, vinculo, requiresLocal) => {
  if (requiresLocal) return false;
  if (decisao !== 'APROVADO') return false;
  return !!(vinculo && vinculo.tipoItem === 'PRODUTO');
};

const loadGroupedLivres = async (tipoEquipamento) => {
  const all = [];
  let page = 0;
  let totalRows = null;
  while (all.length < GROUPED_MAX_ROWS) {
    const result = await fetchEquipamentosGrouped(page, GROUPED_PAGE_SIZE, {
      tipoEquipamento,
      posseEletropontes: 'true',
    });
    const rows = result.rows || [];
    all.push(...rows);
    totalRows = result.totalRows;
    if (!rows.length || all.length >= totalRows) break;
    page += 1;
  }
  return all.slice(0, GROUPED_MAX_ROWS).map((row) => ({
    ...row,
    unidades: (row.unidades || []).filter((unidade) => unidade.comEletropontes === true),
  })).filter((row) => row.unidades.length > 0);
};

const sameTamanho = (left, right) =>
  String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();

const formatQty = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return Number.isInteger(num) ? String(num) : String(num);
};

const loadLocaisProduto = async (produtoId, tamanho) => {
  if (!produtoId) return [];
  const result = await fetchEstoqueGrouped(0, 200, { produtoIds: [produtoId] });
  const row = (result.rows || [])[0];
  if (!row) return [];
  const byLocal = new Map();
  (row.variantes || []).forEach((variante) => {
    if (tamanho && !sameTamanho(variante.produtoTamanhoNome, tamanho)) return;
    const qty = Number(variante.quantidade) || 0;
    if (qty <= 0 || !variante.local) return;
    byLocal.set(variante.local, (byLocal.get(variante.local) || 0) + qty);
  });
  return Array.from(byLocal.entries()).map(([local, quantidade]) => ({
    id: local,
    nome: `${local} (${formatQty(quantidade)})`,
  }));
};

const modeloDoGrupo = (row) =>
  row.modelo || row.tipoEquipamentoNome || row.tipoEquipamento || 'Sem modelo';

const TIPO_VINCULO_LABEL = {
  PRODUTO: 'produto',
  EQUIPAMENTO: 'equipamento',
};

const DecisaoContextoDialog = ({
  open,
  item,
  itemNome,
  decisao,
  isRedecisao,
  onClose,
  onConfirm,
  confirmLoading,
  requiresLocal,
  localOptions,
  vinculo,
}) => {
  const copy = COPY[decisao] || COPY.APROVADO;
  const needsPedido = decisao === 'PARA_PEDIDO' && itemNeedsPedidoVinculado(item);
  const needsEquipamento = resolveNeedsEquipamento(item, decisao, vinculo);
  const needsLocalAvulso = resolveNeedsLocalAvulso(decisao, vinculo, requiresLocal);
  const tipoEquipamento = resolveTipoEquipamento(item, vinculo);
  const quantidadePedida = Number(item?.quantidade) || 0;
  const displayNome = (vinculo && vinculo.nomeItem) || itemNome || '';
  const dialogTitle = displayNome ? `${copy.title} - ${displayNome}` : copy.title;
  const paragraphs = useMemo(() => {
    if (decisao !== 'APROVADO' || !vinculo) return copy.paragraphs;
    const tipoLabel = TIPO_VINCULO_LABEL[vinculo.tipoItem] || 'item do catálogo';
    const nome = vinculo.nomeItem || displayNome || 'o item do catálogo';
    if (vinculo.tipoItem === 'EQUIPAMENTO') {
      return [
        `O item avulso será vinculado ao ${tipoLabel} "${nome}" e atendido com o estoque.`,
        'Na sequência, escolha os patrimônios livres. Depois, a entrega é criada na aba Entregas.',
      ];
    }
    return [
      `O item avulso será vinculado ao ${tipoLabel} "${nome}" e atendido com o estoque.`,
      'Selecione o local de retirada com saldo. Depois, a entrega é criada na aba Entregas — sozinho ou agrupado com outros aprovados.',
    ];
  }, [copy.paragraphs, decisao, vinculo, displayNome]);

  const [local, setLocal] = useState('');
  const [localError, setLocalError] = useState(false);
  const [vinculoLocais, setVinculoLocais] = useState([]);
  const [vinculoLocaisLoading, setVinculoLocaisLoading] = useState(false);
  const [vinculoLocaisError, setVinculoLocaisError] = useState(false);
  const [pedido, setPedido] = useState(() => emptyPedidoVinculado(item));
  const [pedidoErrors, setPedidoErrors] = useState({});
  const [grupos, setGrupos] = useState([]);
  const [gruposLoading, setGruposLoading] = useState(false);
  const [gruposError, setGruposError] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [equipamentoError, setEquipamentoError] = useState(false);
  const [openPatrimonios, setOpenPatrimonios] = useState(false);

  const locaisDisponiveis = needsLocalAvulso ? vinculoLocais : localOptions;
  const locaisLoading = needsLocalAvulso && vinculoLocaisLoading;
  const locaisError = needsLocalAvulso && vinculoLocaisError;
  const showLocalSelect = requiresLocal || needsLocalAvulso;

  useEffect(() => {
    if (!open) {
      setLocal('');
      setLocalError(false);
      setVinculoLocais([]);
      setVinculoLocaisLoading(false);
      setVinculoLocaisError(false);
      setPedido(emptyPedidoVinculado(item));
      setPedidoErrors({});
      setGrupos([]);
      setGruposLoading(false);
      setGruposError(false);
      setSelectedIds([]);
      setEquipamentoError(false);
      setOpenPatrimonios(false);
      return;
    }
    setPedido(emptyPedidoVinculado(item));
    setPedidoErrors({});
    setSelectedIds([]);
    setEquipamentoError(false);
    setOpenPatrimonios(false);
    setLocal('');
    setLocalError(false);
  }, [open, item]);

  useEffect(() => {
    if (!open || !showLocalSelect) return;
    if (locaisDisponiveis.length === 1) {
      setLocal(locaisDisponiveis[0].id);
    }
  }, [open, showLocalSelect, locaisDisponiveis]);

  useEffect(() => {
    if (!open || !needsLocalAvulso || !vinculo?.idItem) {
      setVinculoLocais([]);
      setVinculoLocaisLoading(false);
      setVinculoLocaisError(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setVinculoLocaisLoading(true);
      setVinculoLocaisError(false);
      try {
        const options = await loadLocaisProduto(vinculo.idItem, item?.tamanho);
        if (cancelled) return;
        setVinculoLocais(options);
      } catch (e) {
        if (!cancelled) {
          setVinculoLocais([]);
          setVinculoLocaisError(true);
        }
      } finally {
        if (!cancelled) setVinculoLocaisLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, needsLocalAvulso, vinculo, item]);

  useEffect(() => {
    if (!open || !needsEquipamento || !tipoEquipamento) {
      setGrupos([]);
      setGruposLoading(false);
      setGruposError(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setGruposLoading(true);
      setGruposError(false);
      try {
        const rows = await loadGroupedLivres(tipoEquipamento);
        if (cancelled) return;
        setGrupos(rows);
      } catch (e) {
        if (!cancelled) {
          setGrupos([]);
          setGruposError(true);
        }
      } finally {
        if (!cancelled) setGruposLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, needsEquipamento, tipoEquipamento]);

  const selectedChips = useMemo(() => {
    if (!selectedIds.length) return [];
    const byId = new Map();
    grupos.forEach((row) => {
      const modelo = modeloDoGrupo(row);
      row.unidades.forEach((unidade) => {
        byId.set(unidade.id, {
          id: unidade.id,
          label: `${unidade.numPatrimonio || '—'} · ${modelo}`,
        });
      });
    });
    return selectedIds.map((id) => byId.get(id)).filter(Boolean);
  }, [grupos, selectedIds]);

  const noLocalAvailable = showLocalSelect
    && !locaisLoading
    && !locaisError
    && !locaisDisponiveis.length;
  const equipamentoIncompleto = needsEquipamento
    && (
      gruposLoading
      || gruposError
      || selectedIds.length !== quantidadePedida
    );
  const disabledConfirm = noLocalAvailable
    || locaisLoading
    || locaisError
    || (needsEquipamento && (gruposLoading || gruposError || !grupos.length));

  const handleConfirm = () => {
    if (needsPedido) {
      const nextErrors = validate(pedido, pedidoSchema) || {};
      setPedidoErrors(nextErrors);
      if (Object.keys(nextErrors).length) return;
      onConfirm({
        pedido: {
          local: pedido.local,
          tipoEntrada: 'FORNECEDOR',
          fornecedor: pedido.fornecedor.trim(),
          produtoId: pedido.produto,
          quantidade: Number(pedido.quantidade),
          valorUnitario: Number(pedido.valorUnitario),
          valorTotal: pedido.valorTotal != null
            ? Number(pedido.valorTotal)
            : Number(pedido.quantidade) * Number(pedido.valorUnitario),
          devolucaoUso: false,
          tamanho: item?.tamanho || null,
        },
      });
      return;
    }
    if (needsEquipamento) {
      if (selectedIds.length !== quantidadePedida) {
        setEquipamentoError(true);
        return;
      }
      onConfirm({ equipamentoIds: selectedIds });
      return;
    }
    if (showLocalSelect) {
      if (!local) {
        setLocalError(true);
        return;
      }
      onConfirm({ local });
      return;
    }
    onConfirm({});
  };

  return (
    <>
      <DialogDefaultV2
        open={open}
        handlerCancel={onClose}
        handlerConfirm={handleConfirm}
        title={dialogTitle}
        confirmMessage={needsPedido ? 'Criar pedido e gravar' : 'Confirmar'}
        cancelMessage="Voltar"
        confirmLoading={confirmLoading}
        confirmLoadingMessage="Gravando…"
        disabledConfirmButton={disabledConfirm || equipamentoIncompleto}
        maxWidth={needsPedido || needsEquipamento ? 'md' : 'sm'}
        fullWidth
        useFullScreen={false}
      >
        <Stack spacing={1.5}>
          {paragraphs.map((text) => (
            <Typography key={text} variant="body2">
              {text}
            </Typography>
          ))}
          {isRedecisao ? (
            <Typography variant="body2">
              Isso substitui a decisão já gravada. Pedido de saída de estoque e pedido
              ligados a este item são estornados ou cancelados, se ainda for possível.
            </Typography>
          ) : null}
          {needsPedido ? (
            <PedidoVinculadoForm
              values={pedido}
              errors={pedidoErrors}
              onChange={setPedido}
            />
          ) : null}
          {needsEquipamento ? (
            <Stack spacing={1}>
              <Typography variant="body2">
                Escolha {quantidadePedida} patrimônio
                {quantidadePedida === 1 ? '' : 's'} livre
                {quantidadePedida === 1 ? '' : 's'} com a Eletropontes.
                Pode misturar modelos.
              </Typography>
              {gruposLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Carregando equipamentos disponíveis…
                </Typography>
              ) : null}
              {gruposError ? (
                <Typography variant="body2" color="error.main">
                  Não foi possível carregar os equipamentos. Volte e tente de novo.
                </Typography>
              ) : null}
              {!gruposLoading && !gruposError && !grupos.length ? (
                <Typography variant="body2" color="warning.main">
                  Não há unidades livres deste tipo com a Eletropontes.
                </Typography>
              ) : null}
              {!gruposLoading && !gruposError && grupos.length ? (
                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<Inventory2OutlinedIcon />}
                    onClick={() => setOpenPatrimonios(true)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {selectedIds.length
                      ? `Alterar equipamentos (${selectedIds.length}/${quantidadePedida})`
                      : `Escolher equipamentos (${selectedIds.length}/${quantidadePedida})`}
                  </Button>
                  {selectedChips.length ? (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {selectedChips.map((chip) => (
                        <Chip
                          key={chip.id}
                          size="small"
                          label={chip.label}
                          onDelete={() => {
                            setSelectedIds((current) =>
                              current.filter((id) => id !== chip.id)
                            );
                            setEquipamentoError(false);
                          }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                  {equipamentoError ? (
                    <FormHelperText error>
                      Selecione exatamente {quantidadePedida} patrimônio
                      {quantidadePedida === 1 ? '' : 's'}.
                    </FormHelperText>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          ) : null}
          {showLocalSelect ? (
            locaisLoading ? (
              <Typography variant="body2" color="text.secondary">
                Carregando locais com saldo…
              </Typography>
            ) : locaisError ? (
              <Typography variant="body2" color="error.main">
                Não foi possível carregar os locais deste produto.
              </Typography>
            ) : noLocalAvailable ? (
              <Typography variant="body2" color="warning.main">
                Não há saldo em nenhum local. Use &quot;Para pedido&quot; para compra externa
                ou vincule a outro produto.
              </Typography>
            ) : (
              <DefaultSelectV2
                label="Local de retirada"
                name="local"
                isRequired
                options={locaisDisponiveis}
                value={local}
                error={localError && !local}
                helperText="Selecione o local de retirada"
                onSelect={(event) => {
                  setLocal(event.target.value);
                  setLocalError(false);
                }}
              />
            )
          ) : null}
        </Stack>
      </DialogDefaultV2>
      <EscolherPatrimoniosDialog
        open={openPatrimonios}
        itemNome={itemNome}
        grupos={grupos}
        loading={gruposLoading}
        error={gruposError}
        quantidadePedida={quantidadePedida}
        selectedIds={selectedIds}
        onClose={() => setOpenPatrimonios(false)}
        onConfirm={(ids) => {
          setSelectedIds(ids);
          setEquipamentoError(false);
          setOpenPatrimonios(false);
        }}
      />
    </>
  );
};

DecisaoContextoDialog.propTypes = {
  open: PropTypes.bool,
  item: PropTypes.object,
  itemNome: PropTypes.string,
  decisao: PropTypes.string,
  isRedecisao: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmLoading: PropTypes.bool,
  requiresLocal: PropTypes.bool,
  localOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    nome: PropTypes.string.isRequired,
  })),
  vinculo: PropTypes.shape({
    tipoItem: PropTypes.string,
    idItem: PropTypes.string,
    nomeItem: PropTypes.string,
  }),
};

DecisaoContextoDialog.defaultProps = {
  open: false,
  item: null,
  itemNome: '',
  decisao: 'APROVADO',
  isRedecisao: false,
  confirmLoading: false,
  requiresLocal: false,
  localOptions: [],
  vinculo: null,
};

export default DecisaoContextoDialog;
